import type { DestroyRef, WritableSignal } from '@angular/core';
import { inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { createGridForm, type GridForm } from '@sites/entity-signal-form';

import type { NormalizedGridEntity } from '@web/types';

import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { HistoryFacade } from '@web/store/history/history.facade';

type GridFormEntry = {
    form: GridForm;
    entitySignal: WritableSignal<NormalizedGridEntity>;
    storeSub: Subscription;
    refCount: number;
};

@Injectable({ providedIn: 'root' })
export class WebGridFormService {
    private readonly injector = inject(Injector);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<string, GridFormEntry>();

    public getOrCreate(gridId: string): GridForm {
        const existing = this.registry.get(gridId);
        if (existing) {
            existing.refCount++;
            return existing.form;
        }

        const entitySignal = signal<NormalizedGridEntity>(undefined as any);

        // NgRx store.select() emits synchronously, so entitySignal is populated immediately
        const storeSub = this.gridsFacade
            .byId$(gridId)
            .pipe(filter(Boolean))
            .subscribe((value) => entitySignal.set(value));

        const form = runInInjectionContext(this.injector, () =>
            createGridForm({
                entity: entitySignal,
                onUpdate: (value) => {
                    const currentData = untracked(() => entitySignal()?.props);

                    this.historyFacade.addBatchUpdateToHistory(
                        'grid',
                        { id: value.id, changes: { props: currentData } },
                        { id: value.id, changes: { props: value.props } }
                    );
                    this.gridsFacade.updateProperties(value.id, { props: value.props });
                },
            })
        );

        // Sync immediately with the store value to avoid null data before the effect fires
        const initialEntity = entitySignal();
        if (initialEntity) {
            form.syncFromEntity(structuredClone(initialEntity));
        }

        this.registry.set(gridId, { form, entitySignal, storeSub, refCount: 1 });
        return form;
    }

    public getOrCreateScoped(entityId: string, destroyRef: DestroyRef): GridForm {
        const form = this.getOrCreate(entityId);
        destroyRef.onDestroy(() => this.release(entityId));
        return form;
    }

    public getForm(gridId: string): GridForm | null {
        return this.registry.get(gridId)?.form ?? null;
    }

    public release(gridId: string): void {
        const entry = this.registry.get(gridId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(gridId);
        }
    }

    public deleteForm(gridId: string): void {
        const entry = this.registry.get(gridId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(gridId);
    }
}
